import { useMemo } from 'react';

import useChartOptions from '@/components/Chart/hooks/options/useChartOptions';
import useSeasonalOptions from '@/components/Chart/hooks/options/useSeasonalOptions';
import useSeasonalChartSeries from '@/components/Chart/hooks/useSeasonalChartSeries';
import type { PreviewChartProps } from '@/features/DataHub/constants/charts';
import { CHART_HEIGHT } from '@/features/DataHub/constants/charts';
import { DATAHUB_DEFAULT_GROUP } from '@/features/DataHub/constants/const';
import type { TemplateChartSeries } from '@/pages/Markets/MarketsComponents/types';

import PreviewChartLayout from './PreviewChartLayout';
import usePreviewOptions from './usePreviewOptions';

const PreviewSeasonalChart = ({ from, symbols }: PreviewChartProps) => {
  const templateChartSeries: TemplateChartSeries[] = useMemo(() => {
    return Array.from(symbols)
      .slice(0, 1)
      .map(symbol => ({
        settings: { type: 'line' as const },
        symbol: {
          group: DATAHUB_DEFAULT_GROUP,
          key: {
            Code: symbol.Code,
            PeriodType: symbol.PeriodType,
            Period: symbol.Period,
            TimeRef: symbol.TimeRef
          }
        }
      }));
  }, [symbols]);

  const { loading, error, chartSeries } = useSeasonalChartSeries(templateChartSeries, {
    from: from || '5y'
  });
  const height = chartSeries ? 300 + 20 * chartSeries.length : undefined;
  const { createOptions } = useSeasonalOptions(CHART_HEIGHT);
  const { options: baseOptions } = useChartOptions(chartSeries, createOptions);
  const previewOptions = usePreviewOptions(baseOptions);
  const options = useMemo(() => {
    if (!previewOptions) return undefined;

    return {
      ...previewOptions,
      chart: { ...previewOptions.chart, height }
    };
  }, [previewOptions, height]);
  return (
    <PreviewChartLayout
      chartSeries={chartSeries}
      chartType='seasonal'
      error={error}
      height={height}
      loading={loading}
      options={options}
    />
  );
};

export default PreviewSeasonalChart;
